import { DisconnectReason } from "livekit-client";

/** Participant attribute the video backend sets just before a cross-room
 *  eviction (#1918). Keep in sync with
 *  services/video/core/services/participants_management.py. It is a bare
 *  marker: attributes reach every participant in the room, so the joined
 *  room's name arrives separately, in a data message only the evicted session
 *  receives (JOINED_OTHER_ROOM_TYPE). */
export const EVICTED_ATTRIBUTE = "weco.evicted";
export const EVICTED_JOINED_OTHER_ROOM = "joined_other_room";

/** What the session learned before it was disconnected. */
export interface EvictionSignals {
  /** The local participant's last-known attributes. */
  attributes: Readonly<Record<string, string>>;
  /** The other call's name, from the private data message, if it arrived. */
  otherRoomName?: string;
}

/** Why the session ended, when it ended because the same account took the
 *  call elsewhere. `roomName` is the other call's display name; empty means
 *  it isn't known and the generic copy is shown. */
export type CallEndReason =
  | { kind: "duplicate" }
  | { kind: "otherRoom"; roomName: string };

/** Maps a server-terminal disconnect onto the post-call variant it needs, or
 *  null for every other ending (they keep the default post-call screen).
 *  A plain PARTICIPANT_REMOVED without the eviction marker is NOT an
 *  other-room eviction (it could be any removal). */
export function endReasonFor(
  reason: DisconnectReason | undefined,
  signals: EvictionSignals | undefined,
): CallEndReason | null {
  if (reason === DisconnectReason.DUPLICATE_IDENTITY) return { kind: "duplicate" };
  if (
    reason === DisconnectReason.PARTICIPANT_REMOVED &&
    signals?.attributes[EVICTED_ATTRIBUTE] === EVICTED_JOINED_OTHER_ROOM
  ) {
    return { kind: "otherRoom", roomName: (signals.otherRoomName ?? "").trim() };
  }
  return null;
}
